/* eslint-disable max-statements */
import { cacheDecorator } from '@ds/swymkit/tools'
import { defineStore } from 'pinia'

import type { CreateCommunityData, ICommunity } from '~/entities/container/community/interfaces/community'
import * as service from '~/entities/container/conversation/api/conversations'
import type { IConversation } from '~/entities/container/conversation/interfaces/conversation'
import { convertMessageIdToTimestamp } from '~/entities/container/conversation/utils/message-id'
import type { IUser } from '~/entities/user/user/interfaces/user'
import { useUsersStore } from '~/entities/user/user/stores/users'

import { useTenantStore } from '~/features-shared/config-manager/stores/tenant'
import { useTemporaryMarks } from '~/features-shared/conversation/composables/temporary-marks'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { getConversationList, getMembers as getThreadMembers } from '~/features-shared/member/api/members-list'
// import { getSections } from '~/features-shared/sections/api/sections'
// import { useSectionsStore } from '~/features-shared/sections/store/sections'

import type { SwymResponse } from '~/interfaces/services-dto'

export const useConversationsStoreV1 = defineStore('conversations-v1', () => {
  let marksFetched = false

  const storeTenants = useTenantStore()
  const storeUsers = useUsersStore()
  const { setTempMarks, setFirstMarkSet } = useTemporaryMarks()
  // TODO: Uncomment in 25xFD03
  // const storeSections = useSectionsStore()

  // #region STATES
  const threadsId = reactive<string[]>([])
  const threads = ref<Map<string, IConversation>>(new Map())

  // #endregion

  // #region METHODS
  const getSubjectUriFromId = (id: string) => (threadsId.find(el => el.endsWith(`:${id}`)) || id) as SUBJECT_6W_URI
  const getConversationBySubjectUri = (subject_uri: SUBJECT_6W_URI) => threads.value.get(subject_uri)
  const getConversationById = (id: string) => getConversationBySubjectUri(getSubjectUriFromId(id as SUBJECT_6W_URI))
  const getConversationTitle = (id: string, user_list?: boolean) => {
    const dm = getConversationById(id)

    if (dm?.topic && !user_list)
      return dm.topic

    const users = (
      dm?.users.length === 1
        ? dm?.users
        : dm?.users.filter(el => el.login !== storeUsers.currentUser?.login)
    ) || []
    return users.map(el => `${el.first_name} ${el.last_name}`)
      .join(', ')
  }

  /**
   * Get conversation last message timestamp.
   * @param el - Conversation
   * @returns Timestamp
   */
  const getConversationTime = (el: IConversation) => convertMessageIdToTimestamp(el.last_message?.message_id)
  /** Is conversation unread */
  function isConversationUnread(el: IConversation): boolean {
    return el.last_message
      ? convertMessageIdToTimestamp(el.mark?.message_id) < convertMessageIdToTimestamp(el.last_message?.message_id)
      : false
  }
  // #endregion

  // #region GETTERS
  const threadsVisible = computed(() => [...threads.value.values()].filter(el => !el.is_hidden))
  const countUnread = computed(() => threadsVisible.value.filter(isConversationUnread).length)
  const maxAllowedUsers = computed(() => storeTenants.options?.config.tenant.max_number_conversation_members || 15)
  // #endregion

  // #region ACTIONS
  const doFilter = async (filter: string) => {
    if (filter) {
      return [...threads.value.values()].filter(el => `${el.topic || ''} ${getConversationTitle(el.id, true)}`.toLowerCase()
        .includes(filter.toLowerCase()))
        .map(el => el.id)
    }
    else { return undefined }
  }
  const fetchList = async () => {
    const communitiesResult = (await service.getConversations()).result
    const threadsMap = new Map(communitiesResult.map((i) => {
      const conversation = threads.value.get(i.subject_uri) || {}
      // create a new object here to avoid triggering computed on conversation list
      // every time ONE conversation is updated, so the computed run only once on the map ref update
      return [i.subject_uri, { ...conversation, ...i }]
    }))
    threadsId.push(...communitiesResult.map(i => i.subject_uri))

    try {
      if (!marksFetched) {
        const marksData = (await service.getConversationsMarks()).result.communities // It is "communities" and not "directmessages"
        // Add the marks
        marksData.forEach((i) => {
          const conversation = threadsMap.get(i.subject_uri)
          if (conversation) {
            conversation.mark = i.mark
            conversation.last_message = i.last_message
            threadsMap.set(i.subject_uri, conversation)
          }
        })

        marksFetched = true
      }
    }
    catch { }

    threads.value = threadsMap
    // TODO: code will uncomment in 25xFD03
    // const sections = (await getSections('conversation'))
    // storeSections.sections.set('dm', new Map(sections.map(item => [item.id, item])))
  }
  const fetchConversation = async (id: string) => {
    const conversation = (await service.getConversation(id)).result
    const c = getConversationById(id) || { is_hidden: false }
    threads.value.set(conversation.subject_uri, Object.assign(c, conversation))
    if (!threadsId.includes(conversation.subject_uri))
      threadsId.push(conversation.subject_uri)

    return threads.value.get(conversation.subject_uri)
  }

  const markAsRead = async (uri: SUBJECT_6W_URI) => {
    const conversation = getConversationById(uri)

    // Return early if there's no conversation
    if (!conversation)
      return

    const lastMessageId = conversation.last_message?.message_id

    if (lastMessageId && (lastMessageId !== conversation.mark?.message_id)) {
      setTempMarks(conversation)
      const tmp = conversation.mark
      try {
        conversation.mark = conversation.last_message
        // This API is part of a mutualizer and may wait for upto 10s
        await service.markAsRead({
          messageId: lastMessageId,
          communityUri: uri,
        })
      }
      catch {
        conversation.mark = tmp
      }
    }
    // Set the mark only if we have the mark data
    if (conversation.mark)
      setFirstMarkSet()
  }

  const doCreateConversation = async (users: string[], title?: string) => {
    const { result } = await service.createConversation(users, title)
    const c = getConversationBySubjectUri(result.subject_uri) || {}
    result.is_hidden = false
    threads.value.set(result.subject_uri, Object.assign(c, result))
    if (!threadsId.includes(result.subject_uri))
      threadsId.push(result.subject_uri)
    return result
  }

  const getMembers = async (id: string, page: number, search?: string, refine?: string[]) => {
    if (refine || search) {
      const { users, total } = await getThreadMembers({
        community_id: id,
        query: '#all',
        ...search && { query: search },
        ...refine && { refine },
      })
      return { users, total }
    }
    else {
      const { users, total } = await getConversationList({ community_id: id, page, cache: false })
      return { users, total }
    }
  }
  const doAddMembersToConversation = async (id: string, users: string[]) => {
    const { result } = await service.addMembersConversation(id, users)
    const c = getConversationBySubjectUri(result.subject_uri) || {}
    threads.value.set(result.subject_uri, Object.assign(c, result))
    return result
  }
  const doRemoveMemberFromConversation = async (id: string, user: IUser) => {
    const { result } = await service.removeMembersConversation(id, [user.login])
    return result
  }

  const hideConversation = async (id: string, hide = true) => {
    const conversation = getConversationById(id) as IConversation
    if (conversation) {
      await service.hideConversation(id, hide)
      conversation.is_hidden = hide
    }
  }

  const setTopic = async (id: string, topic: string) => {
    const c = getConversationById(id) || {}
    const conversation = (await service.setTopic(id, topic)).result
    const subject_uri = getSubjectUriFromId(id)
    threads.value.set(subject_uri, Object.assign(c, conversation))
    if (!threadsId.includes(subject_uri))
      threadsId.push(subject_uri)
  }

  const setPersistency = async (id: string, is_persistent: boolean) => {
    const conversation = getConversationById(id) as IConversation
    if (conversation) {
      await service.setPersistency(id, is_persistent)
      conversation.is_persistent = is_persistent
    }
  }

  const transformDmToCommunity = async (
    data: CreateCommunityData,
    conversation: IConversation,
  ): Promise<SwymResponse<ICommunity>> => {
    const community = await service.transformDmToCommunity(data, conversation.id)
    community.result.is_hidden = false
    const index = threadsId.indexOf(conversation.subject_uri)
    if (index > -1)
      threadsId.splice(index, 1)
    threads.value.delete(conversation.subject_uri)
    return community
  }

  const getConversationIfExists = async (login: string, unhide = true) => {
    const conversation = (await service.getConversationIfExists(login, unhide)).result
    if (conversation) {
      const c = getConversationById(conversation.id) || { is_hidden: false }
      threads.value.set(conversation.subject_uri, Object.assign(c, conversation))
      if (!threadsId.includes(conversation.subject_uri))
        threadsId.push(conversation.subject_uri)

      return threads.value.get(conversation.subject_uri)
    }
    else { return null }
  }

  /**
   * Method that calls the API that lets the Swym backend know that there has been an event from RTC server that should be stored in swym backend
   * @param data Required data
   */
  const logAVCallData = async (
    data: Pick<Parameters<typeof service.logAVCall>[0], 'convId' | 'msgId' | 'serviceId'>,
  ) => {
    const conversation = getConversationById(data.convId)

    if (!conversation)
      return

    service.logAVCall({
      ...data,
      users: conversation.users.map(u => u.login),
    })
  }
  // #endregion

  return {
    // #region STATES
    threads,
    threadsId,
    // #endregion

    // #region COMPUTED
    threadsVisible,
    countUnread,
    maxAllowedUsers,
    // #endregion

    // #region METHODS
    getConversationById,
    getConversationBySubjectUri,
    getConversationTitle,
    getConversationTime,
    getSubjectUriFromId,
    isConversationUnread,
    // #endregion

    // #region ACTIONS
    doFilter,
    doCreateConversation,
    getMembers,
    doAddMembersToConversation,
    doRemoveMemberFromConversation,
    fetchList: () => cacheDecorator('fetch-conversations-v1', fetchList, 60000),
    fetchConversation,
    markAsRead,
    hideConversation,
    setTopic,
    setPersistency,
    transformDmToCommunity,
    getConversationIfExists,
    logAVCallData,
    // #endregion
  }
})
